import { useState } from "react";
import type { Message, Poll } from "@swift/shared";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { IconPoll } from "./icons";

/**
 * Rendert het poll-onderdeel van een bericht. Toont vraag, opties met
 * stem-percentages, totaal aantal stemmen en wie waarop heeft gestemd.
 * Single-choice = radio-gedrag (klik vervangt vorige stem); multi-choice
 * = checkbox-gedrag (klik voegt toe of haalt weg).
 *
 * Server-state is leidend: na een vote of close updaten we lokaal niets
 * direct — het bijgewerkte Message-object komt binnen via socket-event
 * message:edited en de Zustand-store werkt de UI bij.
 */
export default function PollCard({
  poll,
  message,
}: {
  poll: Poll;
  message: Message;
}) {
  const me = useStore((s) => s.me);
  const users = useStore((s) => s.users);
  const [busy, setBusy] = useState(false);

  const closed = poll.closed_at !== null;
  const isOwner = me?.id === message.sender_id || me?.is_admin === true;
  const myVotes = new Set(
    poll.options
      .filter((o) => me && o.votes.includes(me.id))
      .map((o) => o.id),
  );
  const totalVotes = poll.options.reduce((n, o) => n + o.votes.length, 0);

  async function vote(optionId: number) {
    if (closed || busy || !me) return;
    setBusy(true);
    try {
      let nextIds: number[];
      if (poll.allow_multiple) {
        // Toggle: was 'ie aan dan eruit, anders erbij.
        const current = new Set(myVotes);
        if (current.has(optionId)) current.delete(optionId);
        else current.add(optionId);
        nextIds = [...current];
      } else {
        // Single-choice: vervangen.
        nextIds = [optionId];
      }
      if (nextIds.length === 0) {
        await api.clearMyPollVote(poll.id);
      } else {
        await api.votePoll(poll.id, nextIds);
      }
      // Socket-event message:edited werkt de store bij.
    } finally {
      setBusy(false);
    }
  }

  async function close() {
    if (busy) return;
    setBusy(true);
    try {
      await api.closePoll(poll.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-md border border-slate-700 bg-slate-800/60 p-3 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <IconPoll size={16} className="shrink-0 text-slate-400" />
        <span className="font-medium text-slate-100">{poll.question}</span>
        {closed && (
          <span className="ml-auto rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
            Gesloten
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {poll.options.map((o) => {
          const pct = totalVotes > 0 ? (o.votes.length / totalVotes) * 100 : 0;
          const mine = myVotes.has(o.id);
          const names = o.votes
            .map((id) => users[id]?.display_name ?? `Gebruiker #${id}`)
            .slice(0, 5)
            .join(", ");
          const extra = o.votes.length > 5 ? ` +${o.votes.length - 5}` : "";
          return (
            <button
              key={o.id}
              onClick={() => void vote(o.id)}
              disabled={busy || closed}
              title={names ? names + extra : undefined}
              className={`relative w-full overflow-hidden rounded-md border text-left transition disabled:cursor-not-allowed ${
                mine
                  ? "border-accent bg-accent/15"
                  : "border-slate-700 bg-slate-900 hover:border-slate-600 hover:bg-slate-800"
              } ${closed ? "" : "cursor-pointer"}`}
            >
              <div
                className={`absolute inset-y-0 left-0 ${mine ? "bg-accent/25" : "bg-slate-700/40"}`}
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
              <div className="relative flex items-center gap-2 px-3 py-1.5">
                <span className="flex-1 text-slate-100">{o.text}</span>
                {mine && <span className="text-xs text-accent">✓</span>}
                <span className="text-xs text-slate-400">
                  {o.votes.length}
                  {totalVotes > 0 && (
                    <span className="ml-1 text-slate-500">({pct.toFixed(0)}%)</span>
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
        <span>
          {totalVotes} stem{totalVotes === 1 ? "" : "men"}
          {poll.allow_multiple && " · meerdere toegestaan"}
        </span>
        {!closed && isOwner && (
          <button
            onClick={() => void close()}
            disabled={busy}
            className="ml-auto rounded bg-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-600 disabled:opacity-50"
          >
            Sluiten
          </button>
        )}
      </div>
    </div>
  );
}
